<template>
    <div class="warehouse-details">
        <div class="warehouse-details__background">
            <img src="~@/assets/images/dashboard-admin.png" alt="kasanowa" />
        </div>
        <KLayoutWrapper>
            <template #default>
                <div>
                    <div v-if="warehouseItems != null && warehouseItems.name != null" class="warehouse-details__admin-title">{{ warehouseItems.name }}</div>
                    <div>
                        <div class="warehouse-details__wrapper">
                            <div class="warehouse-details__tab-wrapper">
                                <v-tabs v-model="tab" :bg-color="primary800" show-arrows center-active>
                                    <v-tab :id="WAREHOUSE_TAB_DATA" :value="WAREHOUSE_TAB_DATA">{{ $t('warehouse.data') }}</v-tab>
                                    <v-tab v-if="!isAdminWholesaler" :id="WAREHOUSE_TAB_ADMINS" :value="WAREHOUSE_TAB_ADMINS">{{ $t('warehouse.adminsTab') }}</v-tab>
                                    <v-tab :id="WAREHOUSE_TAB_PH" :value="WAREHOUSE_TAB_PH">{{ $t('warehouse.phTab') }}</v-tab>
                                </v-tabs>
                            </div>
                            <div class="warehouse-details__top-btn-wrapper">
                                <KButton
                                    v-if="(isAdminWholesaler && tab !== WAREHOUSE_TAB_ADMINS) || !isAdminWholesaler"
                                    id="open-modal"
                                    :label="tab === WAREHOUSE_TAB_DATA ? $t('warehouse.change') : tab === WAREHOUSE_TAB_ADMINS ? $t('warehouse.addNewAdmin') : $t('warehouse.addNewSalesRep')"
                                    icon="arrowRight"
                                    :secondary="tab !== WAREHOUSE_TAB_DATA"
                                    uppercase
                                    :color="black"
                                    @click="tab === WAREHOUSE_TAB_DATA ? navigateTo(`/warehouse/${router.currentRoute.value.params.id}`) : tab === WAREHOUSE_TAB_ADMINS ? navigateTo('/admin/create') : navigateTo('/sales-rep/create')"
                                />
                            </div>
                        </div>

                        <v-window v-model="tab">
                            <v-window-item :value="WAREHOUSE_TAB_DATA">
                                <WarehouseInfo :warehouse-details="warehouseItems" />
                            </v-window-item>
                            <v-window-item v-if="!isAdminWholesaler" :value="WAREHOUSE_TAB_ADMINS">
                                <WarehouseAdminTable />
                            </v-window-item>
                            <v-window-item :value="WAREHOUSE_TAB_PH">
                                <SalesRepTable is-warehouse-view />
                            </v-window-item>
                        </v-window>
                    </div>
                </div>
            </template>
        </KLayoutWrapper>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import router from '@/router';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import WarehouseApi from '@/api/v1/WarehouseApi';
import variables from '@/assets/scss/modules/variables.module.scss';
import { navigateTo, navigateToWithErrorToast } from '@/hooks/NavigateHooks';
import {
    WAREHOUSE_TAB_ADMINS,
    WAREHOUSE_TAB_DATA,
    WAREHOUSE_TAB_PH
} from '@/hooks/WarehouseHooks';
import type { IWarehouseResponse } from '@/models/WarehouseModel';
import KLayoutWrapper from '@/components/atoms/KLayoutWrapper.vue';
import WarehouseInfo from '@/views/Warehouse/components/WarehouseInfo.vue';
import KButton from '@/components/atoms/KButton.vue';
import WarehouseAdminTable from '@/views/Warehouse/components/WarehouseAdminTable.vue';
import SalesRepTable from '@/components/organisms/SalesRep/SalesRepTable.vue';

const { isAdminWholesaler, userInfo } = storeToRefs(useUserStore());
const { logoutUser } = useUserStore();
const { primary800, black } = variables;

const tab = ref<string>(WAREHOUSE_TAB_DATA);

const loading = ref<boolean>(false);
const warehouseItems = ref<IWarehouseResponse | null>(null);
const warehouseApi = new WarehouseApi();
const warehouseDetails = async() => {
    loading.value = true;
    if (isAdminWholesaler.value && userInfo.value.wholesaler_ids.length === 0) {
        navigateToWithErrorToast('/', t('warehouse.toast.errorWarehouseArray'));
        logoutUser();
        loading.value = false;
        return;
    }
    try {
        const result = await warehouseApi.warehouseDetails(router.currentRoute.value.params.id);
        if (!result.isSuccess) {
            navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('warehouse.toast.errorWarehouseDetailsPage'))
            loading.value = false;
            return;
        }
        warehouseItems.value = result.payload;
    } catch (e) {
        navigateToWithErrorToast(!isAdminWholesaler.value ? '/dashboard' : `/warehouse/details/${userInfo.value.wholesaler_ids[0]}`, t('warehouse.toast.errorWarehouseDetailsPage'))
        loading.value = false;
        return;
    }
    loading.value = false;
};
warehouseDetails();
</script>

<style scoped lang="scss">
.warehouse-details {
    width: 100%;
    position: relative;

    &__background {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 0;
        width: 100%;
        height: 100%;

        img {
            object-fit: cover;
            width: 100%;
            height: calc(100% - 184px);
            min-height: calc(100vh - 184px);

            @include media-breakpoint-up(lg) {
                height: calc(100% - 217px);
                min-height: calc(100vh - 217px);
            }
        }
    }

    &__wrapper {
        @include media-breakpoint-up(lg) {
            display: flex;
            align-items: center;
            width: 100%;
        }
    }

    &__admin-title {
        background-color: $primary-400;
        color: $black;
        font-weight: 1000;
        padding: 8px;
        text-transform: uppercase;
        font-size: 20px;
        text-align: center;
        max-width: 520px;
        margin: 0 auto 20px;

        @include media-breakpoint-up(lg) {
            font-size: 40px;
            padding: 18px 24px;
            max-width: 900px;
        }
    }

    &__name-wrapper {
        display: flex;
        color: $white;
        text-transform: uppercase;
        font-size: 18px;
        justify-content: center;
        margin: 30px 0 56px;
    }

    &__tab-wrapper {
        @include media-breakpoint-up(sm) {
            width: max-content;
        }

        &:deep(.v-btn__content) {
            letter-spacing: 0;
            text-transform: uppercase;
        }
        &:deep(.v-btn--size-default) {
            padding: 0 35px;
        }
        &:deep(.v-slide-group__content) {
            justify-content: center;
        }
        &:deep(.v-tab--selected .v-tab__slider) {
            opacity: 1;
            background-color: $secondary-400;
        }
        &:deep(.v-btn) {
            background-color: rgba($black, 0.65);
        }
        &:deep(.v-tab--selected) {
            opacity: 1;
            background-color: $primary-800;
        }
        &:deep(.v-slide-group__next) {
            background-color: $primary-400;
        }
        &:deep(.v-slide-group__prev) {
            background-color: $primary-400;
        }
    }

    &__top-btn-wrapper {
        margin-top: 10px;
        margin-bottom: 10px;

        @include media-breakpoint-up(lg) {
            margin-top: 0;
            margin-bottom: 0;
            margin-left: auto;
        }
    }
}
</style>
